import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { AutoSaveList, ConfirmDialog, useFormReadOnly } from '../../../../common';
import { TreatmentPlanV3PageContent } from '../components';
import { TREATMENT_PLAN_V3_SECTION_HEADING_SX } from '../../../styles';
import {
  AssessmentBarriersSection,
  AssessmentInfoSection,
  AssessmentMethodsSection,
  AssessmentObservationsSection,
  AssessmentReadinessSection,
  AssessmentSummarySection,
  MaladaptiveBehaviorFields,
  NoMaladaptiveBehaviorCheckbox,
} from './components';
import { EMPTY_ASSESSMENT_BEHAVIOR_VALUES } from './constants';
import {
  useFetchTreatmentPlanV3AssessmentPage,
  useNoMaladaptiveBehaviorConfirm,
  useTreatmentPlanV3AssessmentBehaviors,
  useTreatmentPlanV3AssessmentCrisisGuard,
  useTreatmentPlanV3AssessmentMethods,
  useTreatmentPlanV3AssessmentReadiness,
} from './hooks';
import {
  getAssessmentBehaviorDeleteBlock,
  getAssessmentBehaviorDeleteMessage,
  mapAssessmentBehaviorRowToValues,
  toAssessmentBehaviorFieldPayload,
} from './utils';
import { ASSESSMENT_BEHAVIOR_FORM_SCHEMA } from './validationSchema';

import type {
  ITreatmentPlanV3AssessmentBehavior,
  TreatmentPlanV3AssessmentBehaviorPayload,
} from '../../../../../store/treatmentPlanV3';
import type { AssessmentBehaviorFormValues } from './types';

export default function AssessmentForm() {
  const { retry } = useFetchTreatmentPlanV3AssessmentPage();
  const {
    assessmentPage,
    behaviors,
    noMaladaptiveBehavior,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    updatePage,
    setNoMaladaptiveBehavior,
  } = useTreatmentPlanV3AssessmentBehaviors();
  const {
    methods,
    templates,
    isSaving: isSavingMethods,
    create: createMethod,
    update: updateMethod,
    remove: removeMethod,
    createVisual,
    updateVisual,
    updateVisualFile,
    removeVisual,
  } = useTreatmentPlanV3AssessmentMethods();
  const { isSaving: isSavingReadiness, updateItem: updateReadinessItem } =
    useTreatmentPlanV3AssessmentReadiness();
  const {
    crisisPlans,
    crisisPageReady,
    hasCrisisLoadError,
    isSaving: isCrisisSaving,
    refetch: refetchCrisisPage,
  } = useTreatmentPlanV3AssessmentCrisisGuard();
  const readOnly = useFormReadOnly();
  const {
    isConfirmOpen,
    isBlockedOpen,
    blockedMessage,
    isResolving,
    handleToggle,
    handleConfirm,
    closeConfirm,
    closeBlocked,
  } = useNoMaladaptiveBehaviorConfirm({
    hasSavedBehaviors: behaviors.length > 0,
    refetchCrisisPage,
    clearAll,
    setNoMaladaptiveBehavior,
  });

  let pageBody;
  if (hasLoaded && assessmentPage) {
    pageBody = (
      <>
        <AssessmentInfoSection
          assessmentPage={assessmentPage}
          disabled={isLoading || readOnly}
          isUpdating={isBulkSaving}
          onUpdate={updatePage}
          templates={templates}
        />

        <Typography variant="h5" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
          Maladaptive behavior (if present or reported by parents)
        </Typography>

        <NoMaladaptiveBehaviorCheckbox
          checked={noMaladaptiveBehavior}
          disabled={
            isLoading || readOnly || isSaving || isCrisisSaving || isResolving || !crisisPageReady
          }
          onChange={handleToggle}
        />

        {hasCrisisLoadError ? (
          <Stack gap={1} alignItems="flex-start">
            <Typography>
              Unable to load crisis plans. Behaviors cannot be changed until this succeeds.
            </Typography>
            <Button onClick={retry} variant="contained">
              Retry
            </Button>
          </Stack>
        ) : null}

        {noMaladaptiveBehavior ? null : (
          <AutoSaveList<
            AssessmentBehaviorFormValues,
            ITreatmentPlanV3AssessmentBehavior,
            TreatmentPlanV3AssessmentBehaviorPayload
          >
            addButtonLabel="Add behavior"
            disabled={
              isLoading || isBulkSaving || isCrisisSaving || isResolving || !crisisPageReady
            }
            emptyValues={EMPTY_ASSESSMENT_BEHAVIOR_VALUES}
            entries={behaviors}
            getDeleteBlock={(row) => getAssessmentBehaviorDeleteBlock(row, crisisPlans)}
            getDeleteConfirm={(row) => ({
              title: 'Confirm behavior deletion?',
              message: getAssessmentBehaviorDeleteMessage(row),
            })}
            getRowKey={(row) => row.id}
            getRowLabel={(row) => row.behavior}
            isSaving={isSaving}
            mapRowToValues={mapAssessmentBehaviorRowToValues}
            newEntryLabel="New behavior"
            onCreate={create}
            onDelete={remove}
            onUpdate={update}
            plainDraft
            renderFields={({ formik, disabled, readOnly: fieldsReadOnly, saveField }) => (
              <MaladaptiveBehaviorFields
                disabled={disabled}
                formik={formik}
                readOnly={fieldsReadOnly}
                saveField={saveField}
              />
            )}
            toFieldPayload={toAssessmentBehaviorFieldPayload}
            validationSchema={ASSESSMENT_BEHAVIOR_FORM_SCHEMA}
          />
        )}

        <AssessmentMethodsSection
          disabled={isLoading || readOnly}
          initialAssessmentDate={assessmentPage.initial_assessment_date}
          isSaving={isSavingMethods}
          methods={methods}
          onCreate={createMethod}
          onCreateVisual={createVisual}
          onDelete={removeMethod}
          onDeleteVisual={removeVisual}
          onUpdate={updateMethod}
          onUpdateVisual={updateVisual}
          onUpdateVisualFile={updateVisualFile}
          templates={templates}
        />

        <AssessmentReadinessSection
          assessmentPage={assessmentPage}
          disabled={isLoading || readOnly}
          isSaving={isSavingReadiness || isBulkSaving}
          onUpdateItem={updateReadinessItem}
          onUpdatePage={updatePage}
        />

        <AssessmentSummarySection
          assessmentPage={assessmentPage}
          disabled={isLoading || readOnly}
          isUpdating={isBulkSaving}
          onUpdate={updatePage}
          templates={templates}
        />

        <AssessmentBarriersSection
          barriers={assessmentPage.barriers ?? []}
          disabled={isLoading || readOnly}
          isSaving={isBulkSaving}
          onUpdate={updatePage}
        />

        <AssessmentObservationsSection
          assessmentPage={assessmentPage}
          disabled={isLoading || readOnly}
          isUpdating={isBulkSaving}
          onUpdate={updatePage}
        />

        <ConfirmDialog
          open={isConfirmOpen}
          title="Remove all maladaptive behaviors?"
          message={
            <>
              Selecting "No Maladaptive behavior" will permanently delete every maladaptive behavior
              you've entered.
              <br />
              This action can't be undone.
            </>
          }
          confirmLabel="Remove all"
          confirmLoading={isBulkSaving || isCrisisSaving || isResolving}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
        />

        <ConfirmDialog
          open={isBlockedOpen}
          title="Cannot mark no maladaptive behavior"
          message={blockedMessage}
          confirmLabel="OK"
          hideCancel
          onClose={closeBlocked}
          onConfirm={closeBlocked}
        />
      </>
    );
  } else if (hasLoadError) {
    pageBody = (
      <Stack gap={1} alignItems="flex-start">
        <Typography>Unable to load assessment information. Please try again.</Typography>
        <Button onClick={retry} variant="contained">
          Retry
        </Button>
      </Stack>
    );
  } else {
    pageBody = (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return <TreatmentPlanV3PageContent title="Assessment">{pageBody}</TreatmentPlanV3PageContent>;
}
